import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Form } from '../state-man/models/form.model';
import { FormSalesOrderItem } from '../state-man/models/form-sales-order-item.model';
import { LoggerService } from './logger.service';
import { UiService } from './ui.service';
import { FormPhoto } from '../state-man/models/form-photo.model';

/** The wire payload: the device-only card note stays on the device. */
const uploadBody = ({ copied_from, ...form }: Form) => form

@Injectable({
  providedIn: 'root'
})
export class FormService {

  constructor(
    private apiService: ApiService,
    private uiService: UiService
  ) { }

  // version: the one that came with the forms already held, or '' for none. rows
  // is null when those are still current, and nothing else is downloaded.
  public async getMine (include_children: boolean = true, held: Form[] = [], version: string = '') : Promise<{ version: string, rows: Form[] | null } | null> {  
    // We do this on the backend now.
    include_children = false
    try {      
      const ret = await this.apiService.getVersionedList('form/mine/', version)
      if (!ret) return null
      if (!ret.rows) return { version: ret.version, rows: null }
      const forms = Form.buildMany(ret.rows)

      if (include_children) {
        const promises = []
        for (const form of forms) {
          promises.push(this.getAndSetChildren(form))
        }
        await Promise.all(promises)        
      }

      for (const form of forms) {
        if(form.form_photos_to_download) {
          await this.getAndSetFormPhotos(form, held.find(h => h.id === form.id))
        }
      }

      return { version: ret.version, rows: forms }
    } 
    catch (error) {
      LoggerService.log(error + 'form/mine/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  
  
  generateArray(length: number): number[] {
    return Array.from({ length }, (_, i) => i);
  }

  private async getAndSetChildren(form: Form) {
    const sales_order_items = await this.getFormSalesOrderItems(form.id)
    if (sales_order_items) {
      form.sales_order_items = sales_order_items
    } else {
      throw new Error('Error getting sales order items!')
    }

  }

  private async getFormSalesOrderItems(form_id: number) : Promise<FormSalesOrderItem[] | null> {
    try {
      const ret = await this.apiService.apiReturnsAny('get', 'form/items/' + form_id)      
      return (ret && Array.isArray(ret)) ? FormSalesOrderItem.buildMany(ret) : null
    } 
    catch (error) {
      LoggerService.log(error + 'form/items/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }
  }

  // A photo already on the phone is kept while the server's version of it is
  // unchanged; only new or re-saved photos are downloaded.
  private async getAndSetFormPhotos(form: Form, held?: Form) {
    const photos = []
    for(const form_photo_id of form.form_photos_to_download) {
      const version = form.form_photo_versions?.[form_photo_id]
      const kept = version ? held?.form_photos?.find(p => p.id === form_photo_id && p.version === version
        && !p.is_photo_pending_save && !p.is_photo_pending_delete) : undefined
      const photo = kept ?? await this.getFormPhoto(form_photo_id)
      if (photo) {
        photos.push(photo)
      } else {
        throw new Error('Error getting photos!')
      }
    }
    
    form.form_photos = photos
  }

  public async getFormPhoto(form_photo_id: number) : Promise<FormPhoto | null> {
    try {
      const ret = await this.apiService.apiReturnsAny('get', 'form/photos/' + form_photo_id)      
      return ret ? await FormPhoto.buildOne(ret) : null
    } 
    catch (error) {
      LoggerService.log(error + 'form/photos/')
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }
  }

  public async addForm(form: Form) : Promise<Form | null> {
    try {
      const body = uploadBody(form)
      return await this.apiService.apiReturnsAny('post', 'form/', body, 'application/json')   
    } 
    catch (error) {
      LoggerService.log(error + 'post form/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }   

  public async updateForm(form: Form) : Promise<Form | null> {
    try {
      const body = uploadBody(form)
      const ret = await this.apiService.apiReturnsAny('put', 'form/' + form.id, body, 'application/json')         
      return ret
    } catch (error) {
      LoggerService.log(error + 'put form/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async deleteForm(form: Form) : Promise<any> {
    try {
      const body = form
      const ret = await this.apiService.apiReturnsAny('delete', 'form/' + form.id, body, 'application/json')               
      return ret
    } catch (error) {
      LoggerService.log(error + 'delete form/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  
}
